<script setup>
import { ref, computed, toRef, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import {
  ALL_FOLDERS,
  UNFILED_FOLDER,
  templateLinkKey,
  useTemplateFolders,
} from 'dashboard/composables/useTemplateFolders';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import TemplateFolderEditor from './TemplateFolderEditor.vue';
import TemplateFolderDeleteDialog from './TemplateFolderDeleteDialog.vue';
import TemplateMoveToFolder from './TemplateMoveToFolder.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
});
const emit = defineEmits(['onSelect']);
const ALL_CATEGORY = 'ALL';
const OTHER_CATEGORY = 'OTHER';
const KNOWN_CATEGORIES = ['MARKETING', 'UTILITY'];

const { t } = useI18n();
const store = useStore();
const query = ref('');
const selectedCategory = ref(ALL_CATEGORY);
const isRefreshing = ref(false);
const folderFormMode = ref(null);
const folderFormName = ref('');
const folderFormError = ref('');
const folderFormSaving = ref(false);
const editingFolderId = ref(null);
const isDeletingFolder = ref(false);
const deleteFolderError = ref('');
const isMoving = ref(false);
const moveErrorKey = ref('');
const moveError = ref('');
const pendingMove = ref(null);
const operationGeneration = ref(0);
const folderActionsMobileEl = ref(null);
const folderActionsDesktopEl = ref(null);
const deleteDialog = ref(null);

const whatsAppTemplateMessages = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  toRef(props, 'inboxId')
);

const {
  folders,
  canManage,
  foldersAvailable,
  loadError,
  selectedFolder,
  folderIdFor,
  folderNameFor,
  matchesFolder,
  loadFolders,
  createFolder,
  renameFolder,
  deleteFolder,
  assignTemplate,
  selectFolder,
} = useTemplateFolders(toRef(props, 'inboxId'));

const normalizeSearch = value =>
  String(value || '')
    .replace(/_/g, ' ')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const getTemplateBody = template => {
  return findComponentByType(template, COMPONENT_TYPES.BODY)?.text || '';
};

const getTemplateHeader = template => {
  return findComponentByType(template, COMPONENT_TYPES.HEADER);
};

const hasMediaContent = template => {
  const header = getTemplateHeader(template);
  return header && MEDIA_FORMATS.includes(header.format);
};

const displayName = template =>
  String(template?.name || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const templateCategoryKey = template => {
  const value = String(template?.category || '').toUpperCase();
  if (KNOWN_CATEGORIES.includes(value)) return value;
  return OTHER_CATEGORY;
};

const categoryLabel = key => {
  if (key === ALL_CATEGORY) {
    return t('WHATSAPP_TEMPLATES.PICKER.CATEGORIES.ALL');
  }
  if (key === 'MARKETING') {
    return t('WHATSAPP_TEMPLATES.PICKER.CATEGORIES.MARKETING');
  }
  if (key === 'UTILITY') {
    return t('WHATSAPP_TEMPLATES.PICKER.CATEGORIES.UTILITY');
  }
  return t('WHATSAPP_TEMPLATES.PICKER.CATEGORIES.OTHER');
};

const matchesQuery = template => {
  const needle = normalizeSearch(query.value);
  if (!needle) return true;
  return (
    normalizeSearch(template?.name).includes(needle) ||
    normalizeSearch(getTemplateBody(template)).includes(needle)
  );
};

const categoryCounts = computed(() => {
  const counts = { [ALL_CATEGORY]: 0, MARKETING: 0, UTILITY: 0, OTHER: 0 };
  whatsAppTemplateMessages.value.forEach(template => {
    counts[ALL_CATEGORY] += 1;
    counts[templateCategoryKey(template)] += 1;
  });
  return counts;
});

const visibleCategories = computed(() => {
  const keys = [ALL_CATEGORY];
  KNOWN_CATEGORIES.forEach(key => {
    if (categoryCounts.value[key] > 0) keys.push(key);
  });
  if (categoryCounts.value[OTHER_CATEGORY] > 0) keys.push(OTHER_CATEGORY);
  return keys.map(key => ({
    key,
    label: categoryLabel(key),
    count: categoryCounts.value[key],
  }));
});

const folderCounts = computed(() => {
  const counts = { [ALL_FOLDERS]: 0, [UNFILED_FOLDER]: 0 };
  folders.value.forEach(folder => {
    counts[folder.id] = 0;
  });
  whatsAppTemplateMessages.value.forEach(template => {
    counts[ALL_FOLDERS] += 1;
    const folderId = folderIdFor(template);
    if (folderId == null) counts[UNFILED_FOLDER] += 1;
    else counts[folderId] += 1;
  });
  return counts;
});

const visibleFolders = computed(() => {
  if (!foldersAvailable.value) return [];
  return [
    {
      key: ALL_FOLDERS,
      label: t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.ALL'),
      count: folderCounts.value[ALL_FOLDERS],
    },
    {
      key: UNFILED_FOLDER,
      label: t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.UNFILED'),
      count: folderCounts.value[UNFILED_FOLDER],
    },
    ...folders.value.map(folder => ({
      key: folder.id,
      label: folder.name,
      count: folderCounts.value[folder.id] || 0,
    })),
  ];
});

const selectedNamedFolder = computed(() => {
  if (typeof selectedFolder.value !== 'number') return null;
  return (
    folders.value.find(folder => folder.id === selectedFolder.value) || null
  );
});

const selectedFolderSelectValue = computed(() => {
  if (
    selectedFolder.value === ALL_FOLDERS ||
    selectedFolder.value === UNFILED_FOLDER
  ) {
    return selectedFolder.value;
  }
  return String(selectedFolder.value);
});

const showFolderControls = computed(
  () => canManage.value && foldersAvailable.value
);

const filteredTemplateMessages = computed(() =>
  whatsAppTemplateMessages.value.filter(template => {
    const inCategory =
      selectedCategory.value === ALL_CATEGORY ||
      templateCategoryKey(template) === selectedCategory.value;
    return inCategory && matchesQuery(template) && matchesFolder(template);
  })
);

const hasQuery = computed(() => Boolean(normalizeSearch(query.value)));

const showEmptySearch = computed(
  () =>
    hasQuery.value &&
    whatsAppTemplateMessages.value.length > 0 &&
    filteredTemplateMessages.value.length === 0
);

const showEmptyInbox = computed(() => !whatsAppTemplateMessages.value.length);

const beginOperation = () => operationGeneration.value;

const isLiveOperation = generation => generation === operationGeneration.value;

const folderApiError = (error, kind) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const message = error?.response?.data?.message;
  if (
    code === 'folder_name_taken' ||
    (status === 422 && /already exists/i.test(String(message || '')))
  ) {
    return t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.DUPLICATE_NAME');
  }
  if (kind === 'rename') {
    return t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.RENAME_ERROR');
  }
  if (kind === 'delete') {
    return t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.DELETE_ERROR');
  }
  if (kind === 'move') {
    return t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.MOVE_ERROR');
  }
  return t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.CREATE_ERROR');
};

const closeFolderActions = () => {
  folderActionsMobileEl.value?.removeAttribute('open');
  folderActionsDesktopEl.value?.removeAttribute('open');
};

const resetFolderEditor = () => {
  folderFormMode.value = null;
  folderFormName.value = '';
  folderFormError.value = '';
  folderFormSaving.value = false;
  editingFolderId.value = null;
  closeFolderActions();
};

const resetFolderOperations = () => {
  resetFolderEditor();
  isDeletingFolder.value = false;
  deleteFolderError.value = '';
  isMoving.value = false;
  moveErrorKey.value = '';
  moveError.value = '';
  pendingMove.value = null;
  deleteDialog.value?.close();
};

const clearSearch = () => {
  query.value = '';
};

const selectCategory = key => {
  selectedCategory.value = key;
};

const onSelectFolder = key => {
  selectFolder(key);
  closeFolderActions();
};

const onMobileFolderChange = event => {
  const { value } = event.target;
  if (value === ALL_FOLDERS || value === UNFILED_FOLDER) {
    onSelectFolder(value);
    return;
  }
  onSelectFolder(Number(value));
};

const startCreateFolder = () => {
  if (folderFormSaving.value) return;
  folderFormMode.value = 'create';
  editingFolderId.value = null;
  folderFormName.value = '';
  folderFormError.value = '';
};

const startRenameFolder = () => {
  if (folderFormSaving.value || !selectedNamedFolder.value) return;
  folderFormMode.value = 'rename';
  editingFolderId.value = selectedNamedFolder.value.id;
  folderFormName.value = selectedNamedFolder.value.name;
  folderFormError.value = '';
  closeFolderActions();
};

const openDeleteFolder = () => {
  deleteFolderError.value = '';
  closeFolderActions();
  deleteDialog.value?.open();
};

const saveFolder = async () => {
  if (folderFormSaving.value) return;
  const name = folderFormName.value.trim();
  if (!name) {
    folderFormError.value = t(
      'WHATSAPP_TEMPLATES.PICKER.FOLDERS.NAME_REQUIRED'
    );
    return;
  }
  const generation = beginOperation();
  const mode = folderFormMode.value;
  const folderId = editingFolderId.value;
  folderFormSaving.value = true;
  folderFormError.value = '';
  try {
    if (mode === 'rename') {
      if (folderId == null) return;
      const result = await renameFolder(folderId, name);
      if (!isLiveOperation(generation) || result.ignored) return;
    } else {
      const result = await createFolder(name);
      if (!isLiveOperation(generation) || result.ignored) return;
    }
    if (!isLiveOperation(generation)) return;
    folderFormMode.value = null;
    editingFolderId.value = null;
    folderFormName.value = '';
  } catch (error) {
    if (!isLiveOperation(generation)) return;
    folderFormError.value = folderApiError(
      error,
      mode === 'rename' ? 'rename' : 'create'
    );
  } finally {
    if (isLiveOperation(generation)) folderFormSaving.value = false;
  }
};

const confirmDeleteFolder = async () => {
  if (isDeletingFolder.value) return;
  const folder = selectedNamedFolder.value;
  if (!folder) return;
  const generation = beginOperation();
  isDeletingFolder.value = true;
  deleteFolderError.value = '';
  try {
    const result = await deleteFolder(folder.id);
    if (!isLiveOperation(generation) || result.ignored) return;
    deleteDialog.value?.close();
  } catch (error) {
    if (!isLiveOperation(generation)) return;
    deleteFolderError.value = folderApiError(error, 'delete');
  } finally {
    if (isLiveOperation(generation)) isDeletingFolder.value = false;
  }
};

const moveTemplate = async (template, folderId) => {
  if (isMoving.value) return;
  const generation = beginOperation();
  const payload = {
    templateName: template.name,
    language: template.language,
    folderId,
  };
  const key = templateLinkKey(payload.templateName, payload.language);
  isMoving.value = true;
  moveError.value = '';
  moveErrorKey.value = '';
  pendingMove.value = payload;
  try {
    const result = await assignTemplate(payload);
    if (!isLiveOperation(generation) || result.ignored) return;
    pendingMove.value = null;
  } catch (error) {
    if (!isLiveOperation(generation)) return;
    moveErrorKey.value = key;
    moveError.value = folderApiError(error, 'move');
  } finally {
    if (isLiveOperation(generation)) isMoving.value = false;
  }
};

const retryMove = () => {
  if (isMoving.value || !pendingMove.value) return;
  moveTemplate(
    {
      name: pendingMove.value.templateName,
      language: pendingMove.value.language,
    },
    pendingMove.value.folderId
  );
};

const refreshTemplates = async () => {
  if (isRefreshing.value) return;
  isRefreshing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch {
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};

watch(
  () => props.inboxId,
  () => {
    operationGeneration.value += 1;
    query.value = '';
    selectedCategory.value = ALL_CATEGORY;
    resetFolderOperations();
  }
);

watch(visibleCategories, categories => {
  if (!categories.some(category => category.key === selectedCategory.value)) {
    selectedCategory.value = ALL_CATEGORY;
  }
});

defineExpose({
  query,
  selectedCategory,
  selectedFolder,
  filteredTemplateMessages,
  visibleCategories,
  visibleFolders,
  canManage,
  foldersAvailable,
  loadError,
  folderFormMode,
  folderFormName,
  folderFormError,
  editingFolderId,
  isMoving,
  clearSearch,
  selectCategory,
  selectFolder: onSelectFolder,
});
</script>

<template>
  <div class="flex flex-col gap-3 w-full min-w-0">
    <div class="flex gap-2 min-w-0">
      <div
        class="flex flex-1 min-w-0 gap-1 items-center px-2.5 rounded-xl bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
      >
        <span class="i-ph-magnifying-glass size-4 text-n-slate-12 shrink-0" />
        <input
          v-model="query"
          type="search"
          :placeholder="t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          :aria-label="t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          class="reset-base w-full min-w-0 h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
        />
        <button
          v-if="hasQuery"
          type="button"
          class="flex justify-center items-center size-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-n-brand"
          :aria-label="t('WHATSAPP_TEMPLATES.PICKER.CLEAR_SEARCH')"
          @click="clearSearch"
        >
          <Icon icon="i-ph-x" class="size-3.5" />
        </button>
      </div>
      <button
        type="button"
        :disabled="isRefreshing"
        class="flex justify-center items-center w-9 h-9 rounded-xl shrink-0 bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 hover:bg-n-alpha-2 dark:hover:bg-n-solid-2 focus-visible:outline-n-brand disabled:opacity-50 disabled:cursor-not-allowed"
        :title="t('WHATSAPP_TEMPLATES.PICKER.REFRESH_BUTTON')"
        :aria-label="t('WHATSAPP_TEMPLATES.PICKER.REFRESH_BUTTON')"
        :aria-busy="isRefreshing ? 'true' : 'false'"
        @click="refreshTemplates"
      >
        <Icon
          icon="i-ph-arrows-clockwise"
          class="text-n-slate-12 size-4"
          :class="{ 'animate-spin': isRefreshing }"
        />
      </button>
    </div>

    <div
      v-if="loadError"
      class="flex flex-wrap items-center gap-2 rounded-xl bg-n-alpha-2 px-3 py-2"
      data-testid="template-folders-load-error"
    >
      <p class="text-xs text-n-slate-12 min-w-0 flex-1">
        {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.LOAD_ERROR') }}
      </p>
      <button
        type="button"
        data-testid="template-folders-retry"
        class="inline-flex items-center justify-center min-h-10 px-3 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-12 focus-visible:outline focus-visible:outline-n-brand"
        @click="loadFolders"
      >
        {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.RETRY') }}
      </button>
    </div>

    <TemplateFolderEditor
      v-if="showFolderControls && folderFormMode"
      v-model:name="folderFormName"
      :error="folderFormError"
      :is-saving="folderFormSaving"
      :name-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.NAME_LABEL')"
      :save-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.SAVE')"
      :cancel-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.CANCEL')"
      @save="saveFolder"
      @cancel="resetFolderEditor"
    />

    <div v-if="foldersAvailable" class="flex md:hidden flex-col gap-2 min-w-0">
      <p class="text-xs text-n-slate-11">
        {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.LABEL') }}
      </p>
      <div class="flex gap-2 min-w-0 items-center">
        <select
          data-testid="template-folder-select-mobile"
          :aria-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.LABEL')"
          :value="selectedFolderSelectValue"
          class="reset-base flex-1 min-w-0 min-h-10 px-3 text-sm rounded-xl bg-n-alpha-2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          @change="onMobileFolderChange"
        >
          <option
            v-for="folder in visibleFolders"
            :key="`mobile-${folder.key}`"
            :value="String(folder.key)"
          >
            {{ folder.label }}
          </option>
        </select>
        <button
          v-if="showFolderControls"
          type="button"
          data-testid="template-folder-new"
          class="inline-flex items-center justify-center min-h-10 px-3 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-12 shrink-0 focus-visible:outline focus-visible:outline-n-brand"
          @click="startCreateFolder"
        >
          {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.NEW') }}
        </button>
        <details
          v-if="showFolderControls && selectedNamedFolder"
          ref="folderActionsMobileEl"
          class="relative shrink-0"
        >
          <summary
            class="flex items-center justify-center min-h-10 min-w-10 list-none [&::-webkit-details-marker]:hidden rounded-full bg-n-alpha-2 text-n-slate-12 cursor-pointer focus-visible:outline focus-visible:outline-n-brand"
            :aria-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.ACTIONS')"
            data-testid="template-folder-actions"
          >
            <Icon icon="i-ph-dots-three-vertical" class="size-4" />
          </summary>
          <div
            class="absolute right-0 z-20 mt-1 w-44 rounded-xl bg-n-alpha-3 outline outline-1 outline-n-container shadow-lg p-1"
          >
            <button
              type="button"
              data-testid="template-folder-rename"
              class="flex w-full min-h-10 items-center px-3 text-left text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
              @click="startRenameFolder"
            >
              {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.RENAME') }}
            </button>
            <button
              type="button"
              data-testid="template-folder-delete"
              class="flex w-full min-h-10 items-center px-3 text-left text-sm rounded-lg text-n-ruby-11 hover:bg-n-alpha-2"
              @click="openDeleteFolder"
            >
              {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.DELETE') }}
            </button>
          </div>
        </details>
      </div>
    </div>

    <p class="text-xs text-n-slate-11">
      {{ t('WHATSAPP_TEMPLATES.PICKER.CATEGORIES_HINT') }}
    </p>

    <div
      class="flex md:hidden gap-2 overflow-x-auto min-w-0 pb-0.5"
      role="tablist"
      :aria-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.CATEGORIES_LABEL')"
    >
      <button
        v-for="category in visibleCategories"
        :key="`chip-${category.key}`"
        type="button"
        role="tab"
        class="inline-flex items-center gap-1.5 shrink-0 px-3 min-h-10 text-xs font-medium rounded-full outline outline-1 focus-visible:outline-n-brand"
        :class="
          selectedCategory === category.key
            ? 'bg-n-slate-12 text-n-solid-1 outline-transparent'
            : 'bg-n-alpha-2 text-n-slate-12 outline-n-weak hover:bg-n-alpha-2'
        "
        :aria-selected="selectedCategory === category.key ? 'true' : 'false'"
        @click="selectCategory(category.key)"
      >
        <span>{{ category.label }}</span>
        <span class="tabular-nums text-[11px] opacity-80">{{
          category.count
        }}</span>
      </button>
    </div>

    <div
      class="flex min-h-0 min-w-0 gap-3 md:items-stretch md:h-[min(50dvh,28rem)]"
    >
      <div
        class="hidden md:flex md:flex-col md:w-44 md:shrink-0 gap-3 min-h-0 overflow-y-auto"
      >
        <nav
          v-if="foldersAvailable"
          data-testid="template-folders-nav"
          class="flex flex-col gap-1 min-w-0"
          :aria-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.LABEL')"
        >
          <p class="px-3 text-xs font-medium text-n-slate-11 truncate">
            {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.LABEL') }}
          </p>
          <button
            v-for="folder in visibleFolders"
            :key="`folder-${folder.key}`"
            type="button"
            :data-testid="`template-folder-option-${folder.key}`"
            class="flex items-center justify-between gap-2 w-full min-h-10 px-3 text-left text-sm rounded-xl focus-visible:outline focus-visible:outline-n-brand"
            :class="
              selectedFolder === folder.key
                ? 'bg-n-slate-12 text-n-solid-1'
                : 'text-n-slate-12 hover:bg-n-alpha-2'
            "
            :aria-pressed="selectedFolder === folder.key ? 'true' : 'false'"
            @click="onSelectFolder(folder.key)"
          >
            <span class="min-w-0 truncate">{{ folder.label }}</span>
            <span class="tabular-nums text-xs opacity-80 shrink-0">{{
              folder.count
            }}</span>
          </button>
          <button
            v-if="showFolderControls"
            type="button"
            data-testid="template-folder-new-desktop"
            class="flex items-center w-full min-h-10 px-3 text-left text-sm rounded-xl text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-n-brand"
            @click="startCreateFolder"
          >
            {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.NEW') }}
          </button>
          <details
            v-if="showFolderControls && selectedNamedFolder"
            ref="folderActionsDesktopEl"
            class="relative"
          >
            <summary
              class="flex items-center w-full min-h-10 px-3 text-left text-sm rounded-xl text-n-slate-12 cursor-pointer list-none [&::-webkit-details-marker]:hidden hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-n-brand"
              :aria-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.ACTIONS')"
              data-testid="template-folder-actions-desktop"
            >
              {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.ACTIONS') }}
            </summary>
            <div
              class="z-20 mt-1 w-full rounded-xl bg-n-alpha-3 outline outline-1 outline-n-container shadow-lg p-1"
            >
              <button
                type="button"
                data-testid="template-folder-rename-desktop"
                class="flex w-full min-h-10 items-center px-3 text-left text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
                @click="startRenameFolder"
              >
                {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.RENAME') }}
              </button>
              <button
                type="button"
                data-testid="template-folder-delete-desktop"
                class="flex w-full min-h-10 items-center px-3 text-left text-sm rounded-lg text-n-ruby-11 hover:bg-n-alpha-2"
                @click="openDeleteFolder"
              >
                {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.DELETE') }}
              </button>
            </div>
          </details>
        </nav>

        <nav
          data-testid="template-categories-nav"
          class="flex flex-col gap-1 min-w-0"
          :aria-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.CATEGORIES_LABEL')"
        >
          <p class="px-3 text-xs font-medium text-n-slate-11 truncate">
            {{ t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.CATEGORIES_LABEL') }}
          </p>
          <button
            v-for="category in visibleCategories"
            :key="`nav-${category.key}`"
            type="button"
            class="flex items-center justify-between gap-2 w-full min-h-10 px-3 text-left text-sm rounded-xl focus-visible:outline focus-visible:outline-n-brand"
            :class="
              selectedCategory === category.key
                ? 'bg-n-slate-12 text-n-solid-1'
                : 'text-n-slate-12 hover:bg-n-alpha-2'
            "
            :aria-pressed="selectedCategory === category.key ? 'true' : 'false'"
            @click="selectCategory(category.key)"
          >
            <span class="min-w-0 truncate">{{ category.label }}</span>
            <span class="tabular-nums text-xs opacity-80 shrink-0">{{
              category.count
            }}</span>
          </button>
        </nav>
      </div>

      <div
        class="flex-1 min-w-0 min-h-0 bg-n-background outline-n-container outline outline-1 rounded-xl max-h-[min(50dvh,28rem)] overflow-y-auto p-1.5"
      >
        <p
          v-if="selectedNamedFolder"
          class="px-3 pt-2 text-xs text-n-slate-11 truncate min-w-0"
        >
          {{ selectedNamedFolder.name }}
        </p>
        <div
          v-for="template in filteredTemplateMessages"
          :key="`${template.name}:${template.language}`"
          class="flex flex-col md:flex-row items-stretch md:items-start gap-1 min-w-0"
        >
          <button
            type="button"
            data-testid="whatsapp-template-item"
            class="flex gap-3 items-start flex-1 min-w-0 p-3 text-left rounded-xl cursor-pointer hover:bg-n-alpha-2 dark:hover:bg-n-solid-2 focus-visible:outline focus-visible:outline-n-brand"
            @click="emit('onSelect', template)"
          >
            <span class="flex flex-col flex-1 min-w-0 gap-1">
              <span class="flex items-center gap-2 min-w-0">
                <span
                  class="text-sm font-medium leading-5 text-n-slate-12 truncate"
                >
                  {{ displayName(template) }}
                </span>
                <span
                  v-if="hasMediaContent(template)"
                  class="inline-flex items-center justify-center size-6 rounded-lg shrink-0 bg-n-alpha-2 text-n-slate-11"
                  :title="t('WHATSAPP_TEMPLATES.PICKER.MEDIA_INDICATOR')"
                >
                  <Icon
                    icon="i-ph-image"
                    class="size-3.5"
                    :aria-label="t('WHATSAPP_TEMPLATES.PICKER.MEDIA_INDICATOR')"
                  />
                </span>
              </span>
              <span
                class="text-xs leading-4 text-n-slate-11 line-clamp-2 whitespace-pre-wrap"
              >
                {{ getTemplateBody(template) }}
              </span>
              <span
                class="flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-n-slate-10 min-w-0"
              >
                <span>{{ template.language }}</span>
                <span>{{ categoryLabel(templateCategoryKey(template)) }}</span>
                <span
                  v-if="folderNameFor(template)"
                  class="truncate min-w-0"
                  data-testid="template-current-folder"
                >
                  {{
                    t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.CURRENT', {
                      name: folderNameFor(template),
                    })
                  }}
                </span>
              </span>
            </span>
          </button>
          <TemplateMoveToFolder
            v-if="showFolderControls"
            :folders="folders"
            :current-folder-id="folderIdFor(template)"
            :unfiled-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.UNFILED')"
            :move-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.MOVE')"
            :is-saving="isMoving"
            :error="
              moveErrorKey === templateLinkKey(template.name, template.language)
                ? moveError
                : ''
            "
            :retry-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.RETRY')"
            @assign="folderId => moveTemplate(template, folderId)"
            @retry="retryMove"
          />
        </div>

        <div
          v-if="showEmptySearch"
          class="flex flex-col items-center gap-3 px-4 py-8 text-center"
        >
          <p class="text-sm text-n-slate-12">
            {{
              t('WHATSAPP_TEMPLATES.PICKER.NO_SEARCH_RESULTS', {
                query,
              })
            }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center min-h-10 px-3 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-12"
            @click="clearSearch"
          >
            {{ t('WHATSAPP_TEMPLATES.PICKER.CLEAR_SEARCH') }}
          </button>
        </div>
        <div v-else-if="showEmptyInbox" class="px-4 py-8 text-center">
          <p class="text-sm text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_AVAILABLE') }}
          </p>
        </div>
        <div
          v-else-if="!filteredTemplateMessages.length"
          class="px-4 py-8 text-center"
        >
          <p class="text-sm text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
          </p>
        </div>
      </div>
    </div>

    <TemplateFolderDeleteDialog
      ref="deleteDialog"
      :title="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.DELETE_TITLE')"
      :description="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.DELETE_DESCRIPTION')"
      :confirm-label="t('WHATSAPP_TEMPLATES.PICKER.FOLDERS.DELETE_CONFIRM')"
      :is-deleting="isDeletingFolder"
      :error="deleteFolderError"
      @confirm="confirmDeleteFolder"
    />
  </div>
</template>
